import { commaListSchema } from "@birdwatch/common";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { InvoicesList } from "@/features/office/billing/invoices/list";
import {
  filterSearchFragment,
  notificationSearchFragment,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";
import useUserRole from "@/hooks/use-user-role";

const dateRangeSchema = z
  .object({
    start: z.string().optional(),
    end: z.string().optional(),
    label: z.string().optional(),
  })
  .optional()
  .or(z.literal(""));

const priceRangeSchema = z
  .object({
    min: z.coerce.number().optional(),
    max: z.coerce.number().optional(),
  })
  .optional()
  .or(z.literal(""));

const searchSchema = z.object({
  search: z.string().optional(),
  filters: z
    .object({
      status: withOperators(commaListSchema),
      source: withOperators(commaListSchema),
      type: withOperators(commaListSchema),
      accountId: withOperators(commaListSchema),
      total: priceRangeSchema,
      // Accepts the empty-shell "" (chip added before a value is picked) plus
      // the canonical "true"/"false" the boolean widget writes.
      hasBalance: withOperators(
        z
          .union([
            z.literal("true"),
            z.literal("false"),
            z.literal(""),
            z.boolean(),
          ])
          .optional(),
      ),
      date: dateRangeSchema,
      dueDate: dateRangeSchema,
      nextPaymentDate: dateRangeSchema,
      createdAt: dateRangeSchema,
      ...notificationSearchFragment,
    })
    .optional(),
  ...filterSearchFragment,
  sort: z.string().optional(),
  invoiceId: z.string().optional(),
  ...maskSearchFragment,
});

export const Route = createFileRoute("/office/billing/invoices/")({
  validateSearch: searchSchema,
  component: BillingInvoicesPage,
});

function BillingInvoicesPage() {
  const { isAdmin, isManager, isCoordinator, isLoading } = useUserRole();
  const navigate = useNavigate();
  const canAccessInvoices = isAdmin || isManager || isCoordinator;

  useEffect(() => {
    if (!isLoading && !canAccessInvoices) {
      navigate({ to: "/office/tasks" });
    }
  }, [isLoading, canAccessInvoices, navigate]);

  if (isLoading || !canAccessInvoices) {
    return null;
  }

  return <InvoicesList />;
}
